import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Select,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { DeliveryFragment } from "../../../../entity/DeliveryFragment";

interface PropsFragment {
  fragments: DeliveryFragment[];
  setFragments: (fragments: DeliveryFragment[]) => void;
  handleDeleteFragment: (index: number) => void;
}

export const Fragments: React.FC<PropsFragment> = ({
  fragments,
  setFragments,
  handleDeleteFragment,
}) => {
  const { t } = useTranslation();
  return (
    <>
      {fragments.map((fragment, index) => (
        <Box
          key={index}
          p={4}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
          _hover={{ borderColor: "blue.300", shadow: "md" }}
          transition="all 0.2s"
        >
          <VStack spacing={4} align="stretch">
            {/* Header with index and delete button */}
            <HStack justify="space-between" align="center">
              <HStack spacing={2}>
                <Box
                  w="8"
                  h="8"
                  bg="blue.100"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text fontSize="sm" fontWeight="bold" color="blue.600">
                    {index + 1}
                  </Text>
                </Box>
                <Text fontWeight="semibold" color="gray.700">
                  {t("deliveryFragment.label")} #{index + 1}
                </Text>
              </HStack>
              <IconButton
                aria-label="Delete fragment"
                icon={<DeleteIcon />}
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={() => {
                  handleDeleteFragment(index);
                }}
              />
            </HStack>

            {/* Fragment fields */}
            <Grid templateColumns="1fr 1fr" gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("deliveryFragment.label")}
                </FormLabel>
                <Input
                  value={fragment.label}
                  onChange={(e) => {
                    const updatedFragments = [...fragments];
                    updatedFragments[index] = {
                      ...fragment,
                      label: e.target.value,
                    };
                    setFragments(updatedFragments);
                  }}
                  placeholder={t("deliveryFragment.enterLabel")}
                  size="sm"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("deliveryFragment.key")}
                </FormLabel>
                <Input
                  value={fragment.key}
                  onChange={(e) => {
                    const updatedFragments = [...fragments];
                    updatedFragments[index] = {
                      ...fragment,
                      key: e.target.value,
                    };
                    setFragments(updatedFragments);
                  }}
                  placeholder={t("deliveryFragment.enterKey")}
                  size="sm"
                />
              </FormControl>
            </Grid>

            <Grid templateColumns="1fr 1fr 1fr" gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("deliveryConfig.type")}
                </FormLabel>
                <Select
                  value={fragment.type}
                  onChange={(e) => {
                    const updatedFragments = [...fragments];
                    updatedFragments[index] = {
                      ...fragment,
                      type: e.target.value,
                    };
                    setFragments(updatedFragments);
                  }}
                  size="sm"
                >
                  <option value="text">
                    {t("deliveryFragment.type.text")}
                  </option>
                  <option value="number">
                    {t("deliveryFragment.type.number")}
                  </option>
                  <option value="boolean">
                    {t("deliveryFragment.type.boolean")}
                  </option>
                  <option value="select">
                    {t("deliveryFragment.type.select")}
                  </option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("deliveryFragment.defaultValue")}
                </FormLabel>
                {fragment.type === "boolean" ? (
                  <Select
                    value={fragment.defaultValue}
                    onChange={(e) => {
                      const updatedFragments = [...fragments];
                      updatedFragments[index] = {
                        ...fragment,
                        defaultValue: e.target.value,
                      };
                      setFragments(updatedFragments);
                    }}
                    size="sm"
                  >
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </Select>
                ) : fragment.type === "select" ? (
                  <Select
                    value={fragment.defaultValue}
                    onChange={(e) => {
                      const updatedFragments = [...fragments];
                      updatedFragments[index] = {
                        ...fragment,
                        defaultValue: e.target.value,
                      };
                      setFragments(updatedFragments);
                    }}
                    size="sm"
                  >
                    <option value="governorate">
                      {t("deliveryFragment.governorate")}
                    </option>
                    <option value="city">{t("deliveryFragment.city")}</option>
                  </Select>
                ) : (
                  <Input
                    type={fragment.type}
                    value={fragment.defaultValue || ""}
                    onChange={(e) => {
                      const updatedFragments = [...fragments];
                      updatedFragments[index] = {
                        ...fragment,
                        defaultValue: e.target.value,
                      };
                      setFragments(updatedFragments);
                    }}
                    placeholder={t("deliveryFragment.enterDefaultValue")}
                    size="sm"
                  />
                )}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("deliveryFragment.isRequired")}
                </FormLabel>
                <Switch
                  isChecked={fragment.isRequired}
                  onChange={(e) => {
                    const updatedFragments = [...fragments];
                    updatedFragments[index] = {
                      ...fragment,
                      isRequired: e.target.checked,
                    };
                    setFragments(updatedFragments);
                  }}
                  size="sm"
                />
              </FormControl>
            </Grid>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("deliveryFragment.entryByCustomer")}
                </FormLabel>
                <Switch
                  isChecked={fragment.entryByCustomer}
                  onChange={(e) => {
                    const updatedFragments = [...fragments];
                    updatedFragments[index] = {
                      ...fragment,
                      entryByCustomer: e.target.checked,
                    };
                    setFragments(updatedFragments);
                  }}
                  size="sm"
                />
              </FormControl>
            </Grid>
          </VStack>
        </Box>
      ))}
    </>
  );
};
